import { Link } from "react-router-dom";
import { Snowflake, ArrowUpRight, ShieldCheck } from "lucide-react";

const FULL_DISCLAIMER =
  "Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital to demonstrate website, lead capture, and automation systems for home service businesses. It is not a real HVAC company and does not dispatch emergency service.";

const SERVICE_COLUMN = [
  { label: "AC Repair", to: "/services/ac-repair" },
  { label: "Maintenance", to: "/services/maintenance" },
  { label: "Replacement", to: "/services/replacement" },
  { label: "Indoor Air Quality", to: "/services/indoor-air-quality" },
];

const AREA_COLUMN = [
  { label: "Cape Coral", to: "/service-areas" },
  { label: "Fort Myers", to: "/service-areas" },
  { label: "Naples", to: "/service-areas" },
  { label: "Bonita Springs", to: "/service-areas" },
  { label: "Estero", to: "/service-areas" },
  { label: "Lee County", to: "/service-areas" },
  { label: "Collier County", to: "/service-areas" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative mt-12 bg-navy text-white">
      <div className="absolute inset-x-0 top-0 h-px bg-sun-stripe" aria-hidden="true" />
      <div className="container-page grid grid-cols-1 gap-10 py-14 md:grid-cols-12 md:gap-8">
        {/* Brand column */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur">
              <Snowflake className="h-4 w-4 text-aqua" />
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-sun" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-display text-xl font-bold tracking-tight">
                Sun Chaser Cooling
              </span>
              <span className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-white/55">
                Southwest Florida
              </span>
            </div>
          </div>
          <p className="mt-5 max-w-md font-display text-lg leading-snug text-white/85">
            Cool air, clear answers, and smarter service requests.
          </p>
          <p
            data-testid="footer-disclaimer"
            className="mt-6 max-w-md rounded-xl border border-white/10 bg-white/[0.04] p-4 text-[13px] leading-relaxed text-white/70"
          >
            <span className="mb-1 inline-flex items-center gap-1.5 text-aqua">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                Demo notice
              </span>
            </span>
            <br />
            {FULL_DISCLAIMER}
          </p>
        </div>

        {/* Services + Areas */}
        <div className="grid grid-cols-2 gap-8 md:col-span-4">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/55">
              Services
            </h4>
            <ul className="mt-4 space-y-2.5">
              {SERVICE_COLUMN.map((s) => (
                <li key={s.label}>
                  <Link
                    to={s.to}
                    data-testid={`footer-service-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/80 transition-colors hover:text-aqua"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white/55">
              Service Areas
            </h4>
            <ul className="mt-4 space-y-2.5">
              {AREA_COLUMN.map((a) => (
                <li key={a.label}>
                  <Link
                    to={a.to}
                    data-testid={`footer-area-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/80 transition-colors hover:text-aqua"
                  >
                    {a.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contractor CTA column */}
        <div className="md:col-span-3">
          <div
            data-testid="footer-contractor-cta"
            className="relative overflow-hidden rounded-2xl border border-sun/30 bg-gradient-to-br from-sun/15 via-sun/5 to-transparent p-5"
          >
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-sun/20 blur-2xl" aria-hidden="true" />
            <span className="pill-dark border-sun/40 text-sun">
              For HVAC owners
            </span>
            <h4 className="mt-3 font-display text-xl font-bold leading-tight text-white">
              Want a system like this for your HVAC company?
            </h4>
            <div className="mt-5 flex flex-col gap-2">
              <a
                href="https://prismforgedigital.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-cta-prismforge"
                className="inline-flex items-center justify-between rounded-full border border-white/25 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-aqua hover:text-aqua"
              >
                Built by PrismForge Digital
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="https://prismforgedigital.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-cta-systems-call"
                className="inline-flex items-center justify-between rounded-full bg-sun px-4 py-2.5 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.01]"
              >
                Request a Free Systems Call
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page flex flex-col items-start justify-between gap-3 py-5 text-xs text-white/55 md:flex-row md:items-center">
          <p>
            © 2026 Sun Chaser Cooling — a{" "}
            <a
              href="https://prismforgedigital.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/80 underline-offset-2 hover:underline"
            >
              PrismForge Digital
            </a>{" "}
            demo.
          </p>
          <p data-testid="footer-disclaimer-bottom" className="max-w-2xl md:text-right">
            Not a real HVAC company. No emergency dispatch. Phone numbers,
            cities, and customer details on this site are illustrative only.
          </p>
        </div>
      </div>
    </footer>
  );
}
